import React, { useState } from 'react';
import { Modal, Button, Typography, Alert, message, Spin, Upload, Card, List, Row, Col, Input, InputNumber, DatePicker, Form } from 'antd';
import { InboxOutlined, EyeOutlined, DeleteOutlined, EditOutlined, CloseOutlined, PlusOutlined, MinusCircleOutlined, SaveOutlined, RobotOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const { Text, Title } = Typography;
const { Dragger } = Upload;

const BillUpload = ({ onDataExtracted, onClose }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [customerAdded, setCustomerAdded] = useState(false);
  const [productsAdded, setProductsAdded] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [addingProducts, setAddingProducts] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [analysisComplete, setAnalysisComplete] = useState(false);

  // File handling functions
  const handleFileChange = (file) => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    const isValidType = allowedTypes.includes(file.type);
    if (!isValidType) {
      message.error('Please upload PDF, Word, Excel, or image files only.');
      return;
    }
    
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('File must be smaller than 10MB.');
      return;
    }
    
    setFileList(prev => [...prev, file]);
  };

  const handleFileRemove = (file) => {
    setFileList(prev => prev.filter(item => item.uid !== file.uid));
  };

  const handleFilePreview = async (file) => {
    if (file.type === 'application/pdf' || file.type.includes('word') || file.type.includes('excel')) {
      message.info('Preview is not available for this file type. The file will be processed when you analyze.');
      return;
    }
    
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj || file);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleAnalyzeBill = async () => {
    if (fileList.length === 0) {
      Modal.warning({
        title: 'No files selected',
        content: 'Please upload at least one file to analyze.',
      });
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      fileList.forEach((file) => {
        formData.append('files', file.originFileObj || file);
      });

      const response = await axios.post('/sale-invoice/analyze-bill', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setExtractedData(response.data);
      setEditableData({
        customer: { ...response.data.customer },
        invoiceDate: response.data.invoiceDate,
        invoiceNumber: response.data.invoiceNumber,
        products: [...(response.data.products || [])]
      });
      setAnalysisComplete(false);
    } catch (error) {
      console.error('Error analyzing bill:', error);
      setError(error.response?.data?.message || 'Failed to analyze bill. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAddCustomer = async () => {
    if (!extractedData.customer || !extractedData.customer.name) {
      message.error('Customer information is missing');
      return;
    }

    setAddingCustomer(true);
    try {
      const response = await axios.post('/customer/', {
        name: extractedData.customer.name,
        phone: extractedData.customer.phone || '',
        address: extractedData.customer.address || ''
      });

      setExtractedData(prev => ({
        ...prev,
        existingCustomer: response.data,
        newCustomers: []
      }));
      setCustomerAdded(true);
      message.success('Customer added successfully!');
    } catch (error) {
      console.error('Error adding customer:', error);
      message.error(error.response?.data?.message || 'Failed to add customer');
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleUseExtractedData = () => {
    const saleData = {
      existingCustomer: extractedData.existingCustomer,
      customer: extractedData.customer,
      invoiceDate: extractedData.invoiceDate,
      invoiceNumber: extractedData.invoiceNumber,
      existingProducts: extractedData.existingProducts || [],
      newProducts: extractedData.newProducts || [],
      newCustomers: extractedData.newCustomers || [],
      newPublishers: extractedData.newPublishers || [],
      newCurrencies: extractedData.newCurrencies || []
    };
    
    onDataExtracted(saleData);
  };

  const handleRetry = () => {
    setExtractedData(null);
    setEditableData(null);
    setError(null);
    setAnalysisComplete(false);
    setIsEditing(false);
  };

  // Edit handlers
  const handleEditCustomer = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        [field]: value
      }
    }));
  };

  const handleEditInvoiceInfo = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditProduct = (index, field, value) => {
    setEditableData(prev => ({
      ...prev,
      products: prev.products.map((product, i) => 
        i === index ? { ...product, [field]: value } : product
      )
    }));
  };

  const handleAddProduct = () => {
    setEditableData(prev => ({
      ...prev,
      products: [...prev.products, {
        isbn: '',
        title: '',
        name: '',
        author: '',
        publisher: '',
        quantity: 1,
        price: 0,
        currency: '₹',
        discount: 0
      }]
    }));
  };

  const handleRemoveProduct = (index) => {
    setEditableData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const handleSaveEdits = () => {
    setExtractedData(prev => ({
      ...prev,
      customer: editableData.customer,
      invoiceDate: editableData.invoiceDate,
      invoiceNumber: editableData.invoiceNumber,
      products: editableData.products
    }));
    setIsEditing(false);
    setAnalysisComplete(false);
    message.success('Changes saved successfully! You can now proceed to analysis again.');
  };

  const handleCancelEdits = () => {
    setEditableData({
      customer: { ...extractedData.customer },
      invoiceDate: extractedData.invoiceDate,
      invoiceNumber: extractedData.invoiceNumber,
      products: [...(extractedData.products || [])]
    });
    setIsEditing(false);
  };

  const handleProceedToAnalysis = async () => {
    const updatedData = {
      ...extractedData,
      customer: editableData.customer,
      invoiceDate: editableData.invoiceDate,
      invoiceNumber: editableData.invoiceNumber,
      products: editableData.products
    };
    
    setExtractedData(updatedData);
    
    try {
      const response = await axios.post('/sale-invoice/analyze-new-items', {
        customer: updatedData.customer,
        products: updatedData.products
      });
      
      setExtractedData(prev => ({
        ...prev,
        existingCustomer: response.data.existingCustomer,
        newCustomers: response.data.newCustomers || [],
        existingProducts: response.data.existingProducts || [],
        newProducts: response.data.newProducts || [],
        newPublishers: response.data.newPublishers || [],
        newCurrencies: response.data.newCurrencies || []
      }));
      
      setAnalysisComplete(true);
      message.success('Analysis complete! Review new items to be added.');
    } catch (error) {
      console.error('Error analyzing new items:', error);
      message.error('Failed to analyze new items. You can still proceed.');
      setAnalysisComplete(true);
    }
  };

  const uploadProps = {
    name: 'files',
    multiple: true,
    fileList,
    accept: '.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx',
    beforeUpload: (file) => {
      handleFileChange(file);
      return false;
    },
    onRemove: handleFileRemove,
    onPreview: handleFilePreview,
  };

  return (
    <Modal
      title={
        <span>
          <RobotOutlined style={{ marginRight: 8 }} />
          AI Invoice Analysis - Upload Files
        </span>
      }
      open={true}
      onCancel={onClose}
      width={1000}
      footer={null}
      destroyOnClose={true}
      maskClosable={false}
      keyboard={false}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: '16px' }}
    >
      <div style={{ padding: '20px 0' }}>
        {!extractedData && (
          <>
            <Card title="Upload Files" style={{ marginBottom: 16 }}>
              <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag files to this area to upload</p>
                <p className="ant-upload-hint">
                  Support PDF, Word (.doc, .docx), Excel (.xls, .xlsx), and image files (JPG, PNG, GIF, BMP, WEBP). Maximum 10MB per file.
                </p>
              </Dragger>

              {fileList.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Uploaded Files ({fileList.length}):</Text>
                  <List
                    size="small"
                    dataSource={fileList}
                    renderItem={(file) => {
                      const actions = [];
                      
                      if (file.type.startsWith('image/')) {
                        actions.push(
                          <Button
                            key="preview"
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => handleFilePreview(file)}
                          >
                            Preview
                          </Button>
                        );
                      }
                      
                      actions.push(
                        <Button
                          key="remove"
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleFileRemove(file)}
                        >
                          Remove
                        </Button>
                      );
                      
                      return (
                        <List.Item actions={actions}>
                          <Text>{file.name}</Text>
                        </List.Item>
                      );
                    }}
                  />
                </div>
              )}
            </Card>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button
                type="primary"
                size="large"
                loading={analyzing}
                onClick={handleAnalyzeBill}
                disabled={fileList.length === 0}
                icon={<RobotOutlined />}
              >
                {analyzing ? 'Analyzing Invoice...' : 'Extract Invoice Data'}
              </Button>
            </div>

            {analyzing && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text>AI is analyzing your invoice...</Text>
                  <br />
                  <Text type="secondary">This may take a few moments</Text>
                </div>
              </div>
            )}

            {error && (
              <Alert
                message="Analysis Failed"
                description={error}
                type="error"
                showIcon
                action={
                  <Button size="small" onClick={handleRetry}>
                    Retry
                  </Button>
                }
                style={{ marginTop: 16 }}
              />
            )}
          </>
        )}

        {extractedData && (
          <>
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Extracted Data</span>
                  <Button
                    type={isEditing ? "default" : "primary"}
                    icon={isEditing ? <CloseOutlined /> : <EditOutlined />}
                    onClick={() => {
                      if (!isEditing) {
                        setAnalysisComplete(false);
                        setExtractedData(prev => ({
                          ...prev,
                          existingCustomer: null,
                          newCustomers: [],
                          existingProducts: [],
                          newProducts: [],
                          newPublishers: [],
                          newCurrencies: []
                        }));
                      }
                      setIsEditing(!isEditing);
                    }}
                    size="small"
                  >
                    {isEditing ? 'Cancel Edit' : 'Edit Data'}
                  </Button>
                </div>
              }
              style={{ marginTop: 16 }}
            >
              <div style={{ marginBottom: 16 }}>
                <Alert
                  message="Invoice Analysis Complete"
                  description={isEditing ? "Edit the extracted data below and save your changes." : "Please review the extracted data below and confirm if it's correct."}
                  type="success"
                  showIcon
                />
              </div>

              {/* Customer Information */}
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>Customer Information</Title>
                {isEditing ? (
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item label="Name">
                        <Input
                          value={editableData?.customer?.name || ''}
                          onChange={(e) => handleEditCustomer('name', e.target.value)}
                          placeholder="Customer name"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Phone">
                        <Input
                          value={editableData?.customer?.phone || ''}
                          onChange={(e) => handleEditCustomer('phone', e.target.value)}
                          placeholder="Phone number"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Address">
                        <Input
                          value={editableData?.customer?.address || ''}
                          onChange={(e) => handleEditCustomer('address', e.target.value)}
                          placeholder="Address"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : (
                  <div>
                    <p><strong>Name:</strong> {extractedData.customer?.name || 'Not found'}</p>
                    <p><strong>Phone:</strong> {extractedData.customer?.phone || 'Not found'}</p>
                    <p><strong>Address:</strong> {extractedData.customer?.address || 'Not found'}</p>
                  </div>
                )}
              </div>

              {/* Invoice Information */}
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>Invoice Information</Title>
                {isEditing ? (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Invoice Date">
                        <DatePicker
                          value={editableData?.invoiceDate ? moment(editableData.invoiceDate) : null}
                          onChange={(date) => handleEditInvoiceInfo('invoiceDate', date ? date.format('YYYY-MM-DD') : '')}
                          style={{ width: '100%' }}
                          placeholder="Select invoice date"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Invoice Number">
                        <Input
                          value={editableData?.invoiceNumber || ''}
                          onChange={(e) => handleEditInvoiceInfo('invoiceNumber', e.target.value)}
                          placeholder="Invoice number"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : (
                  <div>
                    <p><strong>Invoice Date:</strong> {extractedData.invoiceDate || 'Not found'}</p>
                    <p><strong>Invoice Number:</strong> {extractedData.invoiceNumber || 'Not found'}</p>
                  </div>
                )}
              </div>

              {/* Products */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Title level={5}>Products ({editableData?.products?.length || extractedData.products?.length || 0})</Title>
                  {isEditing && (
                    <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddProduct}>
                      Add Product
                    </Button>
                  )}
                </div>
                <List
                  dataSource={isEditing ? editableData.products : extractedData.products}
                  renderItem={(product, index) => (
                    <List.Item>
                      {isEditing ? (
                        <Row gutter={16} style={{ width: '100%' }}>
                          <Col span={4}>
                            <Input
                              placeholder="ISBN"
                              value={product.isbn || ''}
                              onChange={(e) => handleEditProduct(index, 'isbn', e.target.value)}
                            />
                          </Col>
                          <Col span={5}>
                            <Input
                              placeholder="Title"
                              value={product.title || product.name || ''}
                              onChange={(e) => {
                                handleEditProduct(index, 'title', e.target.value);
                                handleEditProduct(index, 'name', e.target.value);
                              }}
                            />
                          </Col>
                          <Col span={4}>
                            <Input
                              placeholder="Author"
                              value={product.author || ''}
                              onChange={(e) => handleEditProduct(index, 'author', e.target.value)}
                            />
                          </Col>
                          <Col span={3}>
                            <InputNumber
                              placeholder="Qty"
                              value={product.quantity || 1}
                              onChange={(value) => handleEditProduct(index, 'quantity', value)}
                              min={1}
                              style={{ width: '100%' }}
                            />
                          </Col>
                          <Col span={4}>
                            <InputNumber
                              placeholder="Price"
                              value={product.price || 0}
                              onChange={(value) => handleEditProduct(index, 'price', value)}
                              min={0}
                              style={{ width: '100%' }}
                            />
                          </Col>
                          <Col span={2}>
                            <Button
                              type="link"
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => handleRemoveProduct(index)}
                            />
                          </Col>
                        </Row>
                      ) : (
                        <div style={{ width: '100%' }}>
                          <Text strong>{product.title || product.name || 'Unknown'}</Text>
                          <br />
                          <Text type="secondary">
                            ISBN: {product.isbn || 'N/A'} | 
                            Author: {product.author || 'N/A'} | 
                            Qty: {product.quantity || 0} | 
                            Price: {product.currency || '₹'} {product.price || 0}
                          </Text>
                        </div>
                      )}
                    </List.Item>
                  )}
                />
              </div>

              {isEditing && (
                <div style={{ textAlign: 'right', marginTop: 16 }}>
                  <Button onClick={handleCancelEdits} style={{ marginRight: 8 }}>
                    Cancel
                  </Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveEdits}>
                    Save Changes
                  </Button>
                </div>
              )}
            </Card>

            {/* Analysis Results */}
            {!isEditing && (
              <Card title="Analysis Results" style={{ marginTop: 16 }}>
                {extractedData.existingCustomer && (
                  <Alert
                    message="Existing Customer Found"
                    description={`Customer "${extractedData.existingCustomer.name}" already exists in the database.`}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {extractedData.newCustomers && extractedData.newCustomers.length > 0 && (
                  <Alert
                    message="New Customer"
                    description={`Customer "${extractedData.newCustomers[0].name}" will be added to the database.`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {extractedData.existingProducts && extractedData.existingProducts.length > 0 && (
                  <Alert
                    message={`${extractedData.existingProducts.length} Existing Products Found`}
                    description="These products already exist in the database."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {extractedData.newProducts && extractedData.newProducts.length > 0 && (
                  <Alert
                    message={`${extractedData.newProducts.length} New Products`}
                    description="These products will be added to the database."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
              </Card>
            )}

            {/* Action Buttons */}
            {!isEditing && (
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Button onClick={handleRetry} style={{ marginRight: 8 }}>
                  Retry
                </Button>
                {!analysisComplete ? (
                  <Button type="primary" onClick={handleProceedToAnalysis}>
                    Proceed to Analysis
                  </Button>
                ) : (
                  <>
                    {extractedData.newCustomers && extractedData.newCustomers.length > 0 && !customerAdded && (
                      <Button
                        type="default"
                        loading={addingCustomer}
                        onClick={handleAddCustomer}
                        style={{ marginRight: 8 }}
                      >
                        Add Customer
                      </Button>
                    )}
                    <Button type="primary" onClick={handleUseExtractedData}>
                      Use Extracted Data
                    </Button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        <Modal
          open={previewVisible}
          title="Image Preview"
          footer={null}
          onCancel={() => setPreviewVisible(false)}
        >
          <img alt="preview" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      </div>
    </Modal>
  );
};

export default BillUpload;

